import React, { useEffect, useState } from 'react';
import './Contact.css';
import {
  FaEnvelope,
  FaTelegram,
  FaTwitter,
  FaGithub,
  FaLinkedin,
  FaFileAlt,
  FaBook,
} from 'react-icons/fa';
import { ContactMe } from '../types';
import { getContactMe } from '../data/localDataService';

interface ContactLink {
  icon: JSX.Element;
  title: string;
  username: string;
  link: string;
  description: string;
}

const Contact: React.FC = () => {
  const [contactMe, setContactMe] = useState<ContactMe | null>(null);

  useEffect(() => {
    let active = true;
    getContactMe().then((data) => {
      if (active) setContactMe(data);
    });
    return () => {
      active = false;
    };
  }, []);

  if (!contactMe) {
    return (
      <div className="contact-container" aria-busy="true" aria-live="polite">
        <h1 className="contact-title">Let&#39;s Connect</h1>
        <p className="contact-status">Loading contact details&hellip;</p>
      </div>
    );
  }

  const contactLinks: ContactLink[] = [
    {
      icon: <FaEnvelope />,
      title: 'Email',
      username: contactMe.email,
      link: `mailto:${contactMe.email}`,
      description: 'For professional inquiries',
    },
    {
      icon: <FaLinkedin />,
      title: 'LinkedIn',
      username: contactMe.name,
      link: contactMe.linkedinLink,
      description: 'Professional network',
    },
    {
      icon: <FaTelegram />,
      title: 'Telegram',
      username: '@sheeki03',
      link: 'https://t.me/sheeki03',
      description: 'Quick conversations',
    },
    {
      icon: <FaTwitter />,
      title: 'Twitter / X',
      username: '@sheeki03',
      link: 'https://x.com/sheeki03',
      description: 'Blockchain insights & thoughts',
    },
    {
      icon: <FaGithub />,
      title: 'GitHub',
      username: 'sheeki03',
      link: 'https://github.com/sheeki03',
      description: 'Open source contributions',
    },
    {
      icon: <FaFileAlt />,
      title: 'Research Writeups',
      username: 'bit.ly/shivom-writeups',
      link: 'https://bit.ly/shivom-writeups',
      description: 'DeFi research & analysis',
    },
    {
      icon: <FaBook />,
      title: 'Crypto Resources',
      username: 'bit.ly/sheeki-notion',
      link: 'https://bit.ly/sheeki-notion',
      description: 'Curated collection of crypto resources & tools',
    },
  ];

  return (
    <div className="contact-container">
      <header className="contact-header">
        <h1 className="contact-title">Let&#39;s Connect</h1>
        <p className="contact-subtitle">
          Reach out for blockchain research collaborations, DeFi discussions, or just to say
          hello!
        </p>
      </header>

      <div className="contact-grid">
        {contactLinks.map((contact) => {
          const external = !contact.link.startsWith('mailto:');
          return (
            <a
              key={contact.title}
              href={contact.link}
              {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              className="contact-card"
            >
              <span className="contact-icon-wrapper" aria-hidden="true">
                {contact.icon}
              </span>
              <span className="contact-info">
                <h2 className="contact-platform">{contact.title}</h2>
                <span className="contact-username">{contact.username}</span>
                <span className="contact-description">{contact.description}</span>
              </span>
              <span className="contact-arrow" aria-hidden="true">
                &rarr;
              </span>
              {external && (
                <span className="visually-hidden">(opens in a new tab)</span>
              )}
            </a>
          );
        })}
      </div>

      <section className="contact-cta" aria-labelledby="cta-heading">
        <div className="cta-content">
          <h2 id="cta-heading">Open to Opportunities</h2>
          <p>
            Currently leading AI agent infrastructure at Impossible Finance, with deep experience
            across DeFi protocols, investment funds, and Web3 infrastructure projects. With 9+
            years in blockchain and a proven track record in tokenomics design and protocol
            research, I bring deep expertise to help projects succeed.
          </p>
          <ul className="cta-badges" aria-label="Highlights">
            <li className="badge">DegenScore #4</li>
            <li className="badge">20+ Projects Advised</li>
            <li className="badge">DeFi Researcher</li>
            <li className="badge">Early Crypto Adopter</li>
          </ul>
          <a className="cta-button" href={`mailto:${contactMe.email}`}>
            Email Me
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
